import React, { useEffect, useState } from "react";

import styled from "styled-components/macro";

import TierPriceEditor from "Payments/TierPricing/TierPriceEditor";
import API from "Signups/API";
import { COST_TYPES } from "Signups/SharedComponents/costTypes";
import { getVerbs } from "Signups/SharedComponents/signupListVerbs";
import { useIsSignupClosed } from "Signups/SharedComponents/useIsSignupClosed";
import {
  getFundItAmountsInCents,
  getTiersHaveChanged,
} from "Signups/SignupView/SignupSheet/utils/fundItAmountsConversions";
import { useSignups } from "Signups/state";

import FnrButton from "UI/FnrButton3/FnrButton";
import FnrFade from "UI/FnrFade/FnrFade";
import { FnrRadioButtonOption } from "UI/FnrRadioButtonOption";
import { ExpandingRadioButtonOption } from "UI/FnrRadioButtonOption/ExpandingRadioButtonOption";
import { colors } from "UI/colors";

import { fundItStateHooks } from "../stateHooks";

// TODO: better handle the case where signupPaymentsCollected - FnrRadioButtonOption currently does not handle being disabled

export default function CostTypeAndAmount() {
  const [
    {
      currentSignupList: { fundIt, isPerSignupCostType, signupListTypeID, ID },
    },
    dispatch,
  ] = useSignups();
  const fund = fundIt?.fund;
  const hasSignupCost = fundIt?.hasSignupCost;
  const initialAmountsPerSignup = fundItStateHooks.useAmounts();

  const amountsInDollars = fundItStateHooks.useAmountsInDollars();

  const commonPostData = fundItStateHooks.useCommonFundItPostData();

  const { isArchived } = useIsSignupClosed();

  // used to revert to in-process editing of signup costs when the user changes the cost type and comes back in one session
  const [savedPerSignupCosts, setSavedPerSignupCosts] = useState();

  const signupPaymentsCollected = fund?.signupPaymentsCollected;

  const isPerSignupSelected = Boolean(isPerSignupCostType);
  const [perSignupCosts, setPerSignupCosts] = useState(amountsInDollars);
  const [initialPerSignupCosts, setInitialPerSignupCosts] =
    useState(amountsInDollars);

  function clearTierUpdates() {
    setPerSignupCosts(initialPerSignupCosts);
  }
  const hasNoCosts = perSignupCosts?.length === 0;

  // useEffect(() => {
  //   setEditablePerSignupCosts(perSignupCosts);
  // }, [perSignupCosts]);

  // useEffect(() => {
  //   console.log("editablePerSignupCosts", editablePerSignupCosts);
  // }, [editablePerSignupCosts]);

  const [tierDataValid, setTierDataValid] = useState(true);

  const [isSavingPerSignupCost, setIsSavingPerSignupCost] = useState(false);
  const [isSavingPerSpot, setIsSavingPerSpot] = useState(false);
  const initialSavedCostType = isPerSignupSelected ? "perSignup" : "perSpot";
  const [savedCostType, setSavedCostType] = useState(); // perSignup | perSpot

  useEffect(() => {
    if (savedCostType) return;
    if (isPerSignupCostType === undefined) return;

    setSavedCostType(initialSavedCostType);
  }, [initialSavedCostType, savedCostType, isPerSignupCostType]);

  const updateStateToPerSpot = () => {
    dispatch({
      type: "setSignupListIsPerSignupCostType",
      payload: false,
    });
    // CHECK
    dispatch({
      type: "setSignupListPerSignupCostTypeAmount",
      payload: "",
    });
    dispatch({
      type: "updateCostType",
      payload: COST_TYPES.PER_SLOT,
    });
    dispatch({
      type: "updateAllSignupListItems",
      key: "amount",
      value: null,
    });
    setSavedPerSignupCosts(perSignupCosts);
    setPerSignupCosts([]);
  };

  const selectPerSpot = () => {
    if (
      !isPerSignupSelected ||
      signupPaymentsCollected ||
      isSavingPerSpot ||
      isArchived
    )
      return;
    // optimistically update state
    updateStateToPerSpot();

    setIsSavingPerSpot(true);
    API.editFundItOptions(ID, {
      ...commonPostData,
      signupListID: ID,
      costType: "PerSlot",
    })
      .post()
      .then((res) => {
        // do nothing. state already optimistically updated
        setSavedCostType("perSpot");
      })
      .catch((err) => {
        alert(
          "Uh oh! Something went wrong... please try again or contact help@flocknote.com"
        );
        selectPerSignup(); // revert optimistic updates
      })
      .finally(() => {
        setIsSavingPerSpot(false);
      });
  };

  // const savePerSpot = () => {
  //   if (signupPaymentsCollected || isSavingPerSpot || isArchived) return;

  //   setIsSavingPerSpot(true);
  //   API.editFundItOptions(ID, {
  //     ...commonPostData,
  //     signupListID: ID,
  //     costType: "PerSlot",
  //   })
  //     .post()
  //     .then((res) => {
  //       // do nothing. state already optimistically updated
  //       dispatch({
  //         type: "setSignupListPerSignupCostTypeAmount",
  //         payload: "",
  //       });
  //       dispatch({
  //         type: "updateCostType",
  //         payload: COST_TYPES.PER_SLOT,
  //       });
  //       dispatch({
  //         type: "updateAllSignupListItems",
  //         key: "amount",
  //         value: null,
  //       });
  //       setSavedCostType("perSpot");
  //     })
  //     .catch((err) => {
  //       alert(
  //         "Uh oh! Something went wrong... please try again or contact help@flocknote.com"
  //       );
  //       selectPerSignup(); // revert optimistic updates
  //     })
  //     .finally(() => {
  //       setIsSavingPerSpot(false);
  //     });
  // };

  const signupCostsHaveChanged = getTiersHaveChanged(
    perSignupCosts,
    initialAmountsPerSignup,
    !isPerSignupCostType,
    hasSignupCost
  );

  const displayPerHouseholdSaveButton =
    signupCostsHaveChanged || savedCostType === "perSpot";
  const displayPerSpotSaveButton =
    savedPerSignupCosts &&
    !isPerSignupCostType &&
    savedCostType === "perSignup";

  function selectPerSignup() {
    if (isPerSignupSelected || signupPaymentsCollected || isArchived) return;

    // TODO Don't let Betty shoot herself in the foot
    //  Warn Betty if she currently has signup items with an associated cost!
    //  Ask her to confirm that the per-item costs she has configured will be lost

    // don't save yet, only save when the cost is input

    dispatch({
      type: "setSignupListIsPerSignupCostType",
      payload: true,
    });
    dispatch({
      type: "updateAllSignupListItems",
      key: "costType",
      value: COST_TYPES.PER_SIGNUP,
    });
    dispatch({
      type: "updateAllSignupListItems",
      key: "hasSignupCost",
      value: true,
    });
    const costsToUse = savedPerSignupCosts
      ? savedPerSignupCosts
      : amountsInDollars;
    setPerSignupCosts(costsToUse);
    setSavedPerSignupCosts(undefined);
    // dispatch({
    //   type: "updateAllSignupListItems",
    //   key: "amount",
    //   value: null,
    // });
  }

  function savePerSignupCosts(e) {
    e.preventDefault();

    if (!tierDataValid) return;
    setIsSavingPerSignupCost(true);

    const amounts = getFundItAmountsInCents(perSignupCosts);

    if (amounts.length === 0) {
      setIsSavingPerSignupCost(false);
      return;
    }

    API.editFundItOptions(ID, {
      ...commonPostData,
      signupListID: ID,
      costType: "PerSignup",
      amounts,
    })
      .post()
      .then((res) => {
        dispatch({
          type: "setSignupListPerSignupCostTypeAmounts",
          payload: amounts,
        });
        setSavedCostType("perSignup");
        setInitialPerSignupCosts(perSignupCosts);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        // setShouldIndicateUpToDate(true);
        setIsSavingPerSignupCost(false);
      });
  }

  const { entity } = getVerbs(signupListTypeID);
  const isNotItemType = entity !== "item";

  return (
    <Wrapper>
      {/* TODO properly handle disabled state within FnrRadioButtonOption */}
      {/* <CostTypeRadioButton
        label={`Charge per ${entity}`}
        isActive={!isPerSignupSelected}
        onClick={selectPerSpot}
        style={{ marginBottom: 12 }}
        iconColor={!isPerSignupSelected ? colors.fnBlue700 : colors.fnGray400}
        subLabel="This setting allows each Signup item carry an individual charge. You have the option to set prices for each item in its respective Fund It tab."
      /> */}
      <ExpandingRadioButtonOption
        label={`Charge per ${entity}`}
        subLabel="This setting allows each Signup item carry an individual charge. You have the option to set prices for each item in its respective Take Payments tab."
        isActive={!isPerSignupSelected}
        iconColor={!isPerSignupSelected ? colors.fnBlue700 : colors.fnGray400}
        onClick={selectPerSpot}
        style={{ marginBottom: 12 }}
      >
        {/* <FnrFade show={displayPerSpotSaveButton}>
          <SaveButton
            color="green"
            loading={isSavingPerSpot}
            onClick={savePerSpot}
            style={{ marginBlock: "8px", marginInlineStart: 0 }}
          >
            Save
          </SaveButton>
        </FnrFade> */}
      </ExpandingRadioButtonOption>

      {/* Don't show if a poll */}
      {signupListTypeID !== 4 && (
        <ExpandingRadioButtonOption
          label="Charge per household"
          isActive={isPerSignupSelected}
          onClick={selectPerSignup}
          iconColor={isPerSignupSelected ? colors.fnBlue700 : colors.fnGray400}
          subLabel="This setting allows you either to charge each household a fixed amount or create pricing tiers."
        >
          <form onSubmit={savePerSignupCosts}>
            <TierPricingWrapper>
              <TierPriceEditor
                tierData={perSignupCosts}
                setTierData={setPerSignupCosts}
                // tierData={perSignupCosts}
                // setTierData={setPerSignupCosts}
                disabled={signupPaymentsCollected}
                setTierDataValid={setTierDataValid}
                entity={entity}
              />
              <TierButtonWrapper>
                {signupCostsHaveChanged && (
                  <CancelButton type="button" onClick={clearTierUpdates}>
                    Cancel
                  </CancelButton>
                )}

                <div style={{ marginLeft: "auto" }}>
                  <FnrFade show={displayPerHouseholdSaveButton}>
                    <SaveButton
                      color="green"
                      loading={isSavingPerSignupCost}
                      disabled={!tierDataValid || hasNoCosts}
                    >
                      Save
                    </SaveButton>
                  </FnrFade>
                </div>
              </TierButtonWrapper>
            </TierPricingWrapper>
          </form>
        </ExpandingRadioButtonOption>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  margin-top: 24px;
  max-width: 600px;
`;

const CostTypeRadioButton = styled(FnrRadioButtonOption)`
  margin: 0;
  font-weight: 400;
`;

const TierPricingWrapper = styled.div`
  display: grid;
  gap: 16px;
  padding-top: 16px;
  max-width: 440px;

  @media screen and (max-width: 480px) {
    margin-left: -27px;
  }
`;

const TierButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const SaveButton = styled(FnrButton)`
  display: block;
  margin-left: auto;
`;

const CancelButton = styled(SaveButton)`
  margin: 0;
`;
